import React from 'react';
import { FileText, Trash2, Download } from 'lucide-react'; // <-- Import the Download icon
import Swal from 'sweetalert2';
import useApi from "../../utils/useApi";

const IdDisplay = ({ user, client, documents, onRefresh, idExpiry = "" }) => {
    const { deleteId } = useApi();

    const formatFileSize = (bytes) => {
        if (bytes === 0 || !bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDelete = async (document) => {
        console.log(document);
        try {
            const result = await Swal.fire({
                                               title: 'Are you sure?',
                                               text: 'This ID document will be permanently deleted!',
                                               icon: 'warning',
                                               showCancelButton: true,
                                               confirmButtonColor: '#dc3545',
                                               cancelButtonColor: '#6c757d',
                                               confirmButtonText: 'Yes, delete it!',
                                               cancelButtonText: 'Cancel'
                                           });

            if (result.isConfirmed) {
                console.log(client);
                const response = await deleteId(client.id, document);

                if (response.success) {
                    onRefresh();
                    await Swal.fire({
                                        title: 'Deleted!',
                                        text: 'ID document has been deleted.',
                                        icon: 'success',
                                        confirmButtonColor: '#198754'
                                    });
                } else {
                    // handle failed delete here
                }
            }
        } catch (error) {
            await Swal.fire({
                                title: 'Error',
                                text: error.message || 'Failed to delete ID document',
                                icon: 'error',
                                confirmButtonColor: '#dc3545'
                            });
        }
    };

    // This function will programmatically trigger a download
    const handleDownload = (doc) => {
        // Create a temporary link and click it to initiate download
        const link = document.createElement('a');
        link.href = doc.url;
        link.target = '_blank';
        link.download = doc.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderFileContent = (doc) => {
        if (doc.url) {
            if (doc.type.includes('pdf')) {
                return (
                    <div className="w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                        <FileText className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">PDF Document</span>
                    </div>
                );
            }
            return (
                <div className="relative pt-[60%] bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                    <img
                        src={doc.url}
                        alt={doc.file_name}
                        className="absolute top-0 left-0 w-full h-full object-contain"
                    />
                </div>
            );
        }

        return (
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                    {doc.type.includes('pdf') ? 'PDF' : doc.type.includes('image') ? 'IMG' : 'DOC'}
                </span>
            </div>
        );
    };

    if (!documents || documents.length === 0) {
        return (
            <div id="id-display" className="p-4 text-center text-gray-500 dark:text-gray-400">
                No identification documents uploaded
            </div>
        );
    }

    return (
        <div id="id-display" className="lg:col-span-3 sm:col-span-3 col-span-3">
            <h1 className="col-span-1 sm:col-span-2 lg:col-span-3 text-lg mb-3">
                Identification
            </h1>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc, index) => (
                    <div
                        key={doc.id || index}
                        className="flex flex-col p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
                    >
                        {renderFileContent(doc)}

                        <div className="mt-4">
                            <div className="flex justify-between items-start">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {doc.file_name}
                                </h3>
                                <div className="flex space-x-2">
                                    {/* Download Button */}
                                    <button
                                        onClick={() => handleDownload(doc)}
                                        className="p-1 text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                                        title="Download document"
                                    >
                                        <Download className="w-4 h-4" />
                                    </button>

                                    {/* Delete Button */}
                                    <button
                                        onClick={() => handleDelete(doc)}
                                        className="p-1 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                                        title="Delete document"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-1 space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Expiry Date</span>
                                    <span className="text-xs text-gray-700 dark:text-gray-300">{client.profile.identification_expiration_date ?? idExpiry}</span>
                                </div>
                                {user.user_type === 'admin' && (
                                    <>
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Type</span>
                                            <span className="text-xs text-gray-700 dark:text-gray-300">{doc.type}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Size</span>
                                            <span className="text-xs text-gray-700 dark:text-gray-300">{formatFileSize(doc.size)}</span>
                                        </div>
                                    </>
                                )}

                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Uploaded</span>
                                    <span className="text-xs text-gray-700 dark:text-gray-300">{formatDate(doc.created_at)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

        </div>
    );
};

export default IdDisplay;
